import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Shield,
  User,
  Mail,
  Calendar,
  Clock
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface InvitationDetails {
  id: string;
  inviter_id: string;
  invited_email: string;
  invited_first_name: string;
  invited_last_name: string;
  invited_phone: string;
  relationship: string;
  invitation_message: string;
  expires_at: string;
  sent_at: string;
  status: string;
  inviter_email?: string;
  inviter_name?: string;
}

const InvitationAcceptance: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      loadInvitation();
    }
  }, [token]);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get invitation details
      const { data: invitationData, error: invitationError } = await supabase
        .from('trusted_advisor_invitations')
        .select('*')
        .eq('invitation_token', token)
        .single();

      if (invitationError) throw invitationError;

      if (!invitationData) {
        setError('Invitation not found');
        return;
      }

      // Check if invitation has expired
      if (new Date(invitationData.expires_at) < new Date()) {
        setError('This invitation has expired');
        return;
      }

      // Check if invitation is already processed
      if (invitationData.status !== 'pending') {
        setError(`This invitation has already been ${invitationData.status}`);
        return;
      }

      // Get inviter details
      const { data: inviterData } = await supabase
        .from('auth.users')
        .select('email, raw_user_meta_data')
        .eq('id', invitationData.inviter_id)
        .single();

      if (inviterData) {
        invitationData.inviter_email = inviterData.email;
        invitationData.inviter_name = inviterData.raw_user_meta_data?.full_name || inviterData.email;
      }

      setInvitation(invitationData);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!invitation) return;

    try {
      setProcessing(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in to accept this invitation');
        return;
      }

      // Check if user email matches invitation
      if (user.email !== invitation.invited_email) {
        setError('This invitation was sent to a different email address');
        return;
      }

      // Process the invitation acceptance
      const { data: success, error: processError } = await supabase.rpc(
        'process_trusted_advisor_invitation',
        invitation.id,
        'accept',
        user.id
      );

      if (processError || !success) {
        throw new Error('Failed to accept invitation');
      }

      setSuccess('Invitation accepted successfully! You are now a trusted advisor.');
      
      // Redirect after a delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation) return;

    try {
      setProcessing(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in to decline this invitation');
        return;
      }

      // Check if user email matches invitation
      if (user.email !== invitation.invited_email) {
        setError('This invitation was sent to a different email address');
        return;
      }

      // Process the invitation decline
      const { data: success, error: processError } = await supabase.rpc(
        'process_trusted_advisor_invitation',
        invitation.id,
        'decline',
        user.id
      );

      if (processError || !success) {
        throw new Error('Failed to decline invitation');
      }

      setSuccess('Invitation declined successfully.');
      
      // Redirect after a delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <div className="p-6 text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Invitation Error
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/dashboard')} variant="primary">
              Go to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <div className="p-6 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Success!
            </h2>
            <p className="text-gray-600 mb-6">{success}</p>
            <p className="text-sm text-gray-500">
              Redirecting to dashboard...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  const isExpired = new Date(invitation.expires_at) < new Date();
  const timeRemaining = formatDistanceToNow(new Date(invitation.expires_at), { addSuffix: true });

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full">
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Trusted Advisor Invitation
            </h1>
            <p className="text-gray-600">
              You've been invited to be a trusted advisor
            </p>
          </div>

          {/* Invitation Details */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Invitation Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <User className="w-4 h-4 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-600">From:</span>
                    <span className="ml-2 font-medium">{invitation.inviter_name || invitation.inviter_email}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-600">Your Email:</span>
                    <span className="ml-2 font-medium">{invitation.invited_email}</span>
                  </div>
                  <div className="flex items-center">
                    <Shield className="w-4 h-4 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-600">Relationship:</span>
                    <span className="ml-2 font-medium">{invitation.relationship}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-600">Sent:</span>
                    <span className="ml-2 font-medium">
                      {formatDistanceToNow(new Date(invitation.sent_at), { addSuffix: true })}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-600">Expires:</span>
                    <span className="ml-2 font-medium">{timeRemaining}</span>
                  </div>
                </div>
              </div>
            </div>

            {invitation.invitation_message && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Personal Message:</h4>
                <p className="text-gray-600 italic">"{invitation.invitation_message}"</p>
              </div>
            )}
          </div>

          {/* Warning if expired */}
          {isExpired && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-700 font-medium">
                  This invitation has expired and can no longer be accepted.
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            {!isExpired ? (
              <>
                <Button
                  onClick={handleAccept}
                  variant="primary"
                  disabled={processing}
                  className="w-full sm:w-auto"
                >
                  {processing ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Accepting...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Accept Invitation
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleDecline}
                  variant="outline"
                  disabled={processing}
                  className="w-full sm:w-auto"
                >
                  {processing ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Declining...</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Decline Invitation
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={() => navigate('/dashboard')} variant="primary">
                Go to Dashboard
              </Button>
            )}
          </div>

          {/* Information */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              By accepting this invitation, you'll be able to access documents and information
              that have been shared with you by {invitation.inviter_name || invitation.inviter_email}.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InvitationAcceptance;
